/**
 * UserDrillDownPage
 *
 * Dedicated landing page for per-user drill-down reports — replaces the old
 * right-side drawer shown on `/#/infralytiqs/reports`. Route:
 *     /#/infralytiqs/reports/:suiteId/:userId
 *
 * Renders every `mode: 'drilldown'` report in the chosen suite, locked to
 * `user_id = :userId` via runtimeFilters. Tables (e.g. "Pages Visited") use
 * the same server-paginated / sortable / filterable `<PaginatedReportTable />`
 * as the "Top Users" panel.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton,
  Stack, Tooltip, Typography, useTheme,
  useMediaQuery,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
} from '@mui/material';
import {
  ArrowBack, Refresh, Menu as MenuIcon,
  AccessTime, ChevronLeft, ChevronRight, ZoomIn, ZoomOut,
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { useModulePermissions } from 'hooks/useModulePermissions';
import apiClient from 'utils/ApiClient';
import { ReportPanel } from './ReportPanel';
import { useReportsTimeRange } from './hooks/useReportsTimeRange';
import { TimePickerPopover } from '../Logs/components/TimePickerPopover';
import { REPORTS_QUICK_RANGES, REPORTS_CUSTOM_TIME_MAX_MS } from './reportsTimeRange';
import {
  reportsTimeRangeIconButtonSx,
  reportsTimeRangePickerButtonSx,
  reportsToolbarIconButtonSx,
  reportsToolbarSurfaceSx,
} from './reportsToolbarSurface';
import {
  FilterDef, ReportSuiteDetail, TimeRangeDef,
  sortReportsByLayout,
} from '../Settings/reportDsl';

interface ModuleConfig {
  _id: string;
  moduleType: string;
  name: string;
  tenantId?: string;
  siteId?: string;
  defaultTimeRange?: string;
  reportSuites?: Array<{ _id: string; name: string; description?: string }>;
}

const msToChDateTime = (ms: number): string =>
  new Date(ms).toISOString().replace('T', ' ').replace('Z', '').slice(0, 23);

export const UserDrillDownPage: React.FC = () => {
  // `reportId` is the parent (level-1) drill-down report whose row click landed
  // us here; present on the deep-linkable route and used for the back action /
  // breadcrumb. Older links without it still work (no parent crumb).
  const { suiteId, reportId, userId } = useParams<{
    suiteId: string;
    reportId?: string;
    userId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Per-panel Edit button is only for infralytiqs.com admins (and super-admins).
  const { hasInfralytiqsAccess, isUserSuperAdmin } = useModulePermissions();
  const canEditReportPanels = hasInfralytiqsAccess('admin') || isUserSuperAdmin;

  const routePrefix = location.pathname.startsWith('/analytics')
    ? '/analytics/reports'
    : '/infralytiqs/reports';

  // Path back to the parent (level-1) panel drill-down, when known.
  const level1Path = reportId ? `${routePrefix}/${suiteId}/drilldown/${reportId}` : undefined;

  /** Navigate back to the parent panel drill-down, or the reports dashboard. */
  const goBack = useCallback(() => {
    navigate(level1Path || routePrefix);
  }, [navigate, level1Path, routePrefix]);

  const [config, setConfig] = useState<ModuleConfig | null>(null);
  const [suite, setSuite] = useState<ReportSuiteDetail | null>(null);
  const [loadingCfg, setLoadingCfg] = useState<boolean>(true);
  const [loadingSuite, setLoadingSuite] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Mobile-only context dialog opened from the burger button. Mirrors the
  // pattern used on /#/infralytiqs/reports — keeps the toolbar compact on
  // phones while still exposing the page context (suite / tenant / site)
  // and a quick "Back to Reports" action.
  const [mobileContextDialogOpen, setMobileContextDialogOpen] = useState(false);

  // Shared, persisted time-range selection (same store as the Reports
  // dashboard / level-1 toolbar) so the chosen window is remembered across
  // navigation and drill-downs until the user changes it.
  const timeRange = useReportsTimeRange({
    scopeKey: config?._id && suiteId ? `${config._id}::${suiteId}` : '',
    defaultTimeRangeLabel: config?.defaultTimeRange || '',
  });
  const pickerDates = timeRange.getPickerInitialDates();

  // Resolve `suiteId` → owning ClickHouse moduleConfig (tenant/site live there).
  const loadConfigs = useCallback(async () => {
    setLoadingCfg(true);
    setErrorMsg(null);
    try {
      const res: any = await apiClient.get('/il/settings/module-configs');
      const list: ModuleConfig[] = Array.isArray(res?.data)
        ? res.data
        : (res?.data?.moduleConfigurations ?? []);
      const owning = list
        .filter((c) => c.moduleType === 'ClickHouse')
        .find((c) => (c.reportSuites || []).some((s) => s._id === suiteId));
      if (!owning) {
        setErrorMsg(`Report suite ${suiteId} was not found on any ClickHouse configuration.`);
        setConfig(null);
        return;
      }
      setConfig(owning);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to load module configurations');
    } finally {
      setLoadingCfg(false);
    }
  }, [suiteId]);

  const loadSuite = useCallback(async () => {
    if (!config || !suiteId) return;
    setLoadingSuite(true);
    setErrorMsg(null);
    try {
      const res: any = await apiClient.get(
        `/il/settings/module-config/${config._id}/report-suites/${suiteId}`,
      );
      setSuite(res?.data ?? null);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to load report suite');
    } finally {
      setLoadingSuite(false);
    }
  }, [config, suiteId]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);
  useEffect(() => { loadSuite(); }, [loadSuite]);

  const drilldownReports = useMemo(
    () => {
      const all = sortReportsByLayout(
        (suite?.reports ?? []).filter(
          (r) =>
            r.mode === 'drilldown' &&
            // Exclude "user list" navigator reports (those whose row-click drills
            // down by user_id). On a page already scoped to a single user they
            // would just show that one user, so they add no value here.
            r.interactions?.onRowClick?.passField !== 'user_id',
        ),
      );
      // When the navigator (level-1) report explicitly declares which panels
      // belong on its per-user page, honour that curated set and order.
      const navigator = reportId
        ? (suite?.reports ?? []).find((r) => r._id === reportId)
        : undefined;
      const wanted = navigator?.interactions?.onRowClick?.level2ReportNames;
      if (wanted && wanted.length > 0) {
        return wanted
          .map((name) => all.find((r) => r.name === name))
          .filter((r): r is NonNullable<typeof r> => Boolean(r));
      }
      return all;
    },
    [suite, reportId],
  );

  const timeRangeOverride: TimeRangeDef = useMemo(() => {
    if (timeRange.activePreset) return { preset: timeRange.activePreset };
    const { start, end } = timeRange.getTimeRangeMs();
    return { start: msToChDateTime(start), end: msToChDateTime(end) };
  }, [timeRange.activePreset, timeRange.getTimeRangeMs]);

  // Filter every drill-down panel by user_id = <URL param>. Using `eq` ensures
  // ClickHouse can use the primary key (user_id, event_time, …) efficiently.
  const runtimeFilters: FilterDef[] = useMemo(() => (
    userId ? [{ field: 'user_id', op: 'eq', value: String(userId) }] : []
  ), [userId]);

  const suiteName = suite?.name || 'Report Suite';

  // The :userId path param arrives URL-encoded (e.g. `mtwaher%40gmail.com`).
  // Decode it for the breadcrumb / page title so users see the real value.
  // Falling back to the raw value protects against malformed `%xx` sequences
  // that would otherwise throw inside `decodeURIComponent`.
  const displayUserId = useMemo(() => {
    if (!userId) return '';
    try {
      return decodeURIComponent(userId);
    } catch {
      return userId;
    }
  }, [userId]);

  // Parent (level-1) report title for the breadcrumb, resolved from the suite.
  const parentTitle = useMemo(() => {
    if (!reportId) return undefined;
    const parent = (suite?.reports ?? []).find((r) => r._id === reportId);
    if (!parent) return undefined;
    return parent.visualizations?.[0]?.title || parent.name;
  }, [suite, reportId]);

  const breadcrumbItems = useMemo(() => {
    // Report Dashboard (clickable) › <level-1 title> (clickable) › User : id
    const items: Array<{ itemPath: string; itemLabel: string }> = [
      { itemPath: routePrefix, itemLabel: 'Report Dashboard' },
    ];
    if (level1Path) {
      // Clickable crumb back to the parent (level-1) panel drill-down.
      items.push({ itemPath: level1Path, itemLabel: parentTitle || 'Drill-down' });
    }
    items.push({ itemPath: '', itemLabel: `User : ${displayUserId}` });
    return items;
  }, [routePrefix, level1Path, parentTitle, displayUserId]);

  return (
    <Box sx={{ pl: { xs: 1.25, md: 2.5 }, pt: 1, width: '100%', mb: 3 }}>
      {/* Navigation is handled by the breadcrumb below the toolbar. */}

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 1, md: 2 },
          alignItems: 'center',
          mb: 3,
          p: { xs: 1, md: 1.5 },
          ...reportsToolbarSurfaceSx,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Tooltip title="Zoom in time range">
            <span>
              <IconButton
                size="small"
                onClick={timeRange.handleZoomIn}
                disabled={timeRange.timeRangeLabel === REPORTS_QUICK_RANGES[0].label}
                sx={reportsTimeRangeIconButtonSx(theme)}
              >
                <ZoomIn fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Shift time range back">
            <IconButton
              size="small"
              onClick={() => timeRange.shiftTime('back')}
              sx={reportsTimeRangeIconButtonSx(theme)}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            color={theme.palette.mode === 'light' ? undefined : 'secondary'}
            onClick={timeRange.handleTimePickerOpen}
            startIcon={<AccessTime sx={{ fontSize: 16 }} />}
            sx={reportsTimeRangePickerButtonSx(theme)}
          >
            {timeRange.timeRangeLabel}
          </Button>
          <Tooltip title="Shift time range forward">
            <IconButton
              size="small"
              onClick={() => timeRange.shiftTime('forward')}
              sx={reportsTimeRangeIconButtonSx(theme)}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Zoom out time range">
            <IconButton
              size="small"
              onClick={timeRange.handleZoomOut}
              sx={reportsTimeRangeIconButtonSx(theme)}
            >
              <ZoomOut fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Tooltip title="Refresh all panels">
          <IconButton
            onClick={loadSuite}
            sx={reportsToolbarIconButtonSx(theme)}
            aria-label="Refresh all panels"
          >
            <Refresh />
          </IconButton>
        </Tooltip>

        {/* Mobile-only burger — opens a dialog with page context + nav. */}
        <Tooltip title="Page details">
          <IconButton
            onClick={() => setMobileContextDialogOpen(true)}
            sx={{ display: { xs: 'inline-flex', md: 'none' }, ...reportsToolbarIconButtonSx(theme) }}
            aria-label="Page details"
          >
            <MenuIcon />
          </IconButton>
        </Tooltip>

        {config && (
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, ml: 'auto' }}>
            <Chip size="small" label={`suite: ${suiteName}`} variant="outlined" />
          </Box>
        )}
      </Box>

      <TimePickerPopover
        anchorEl={timeRange.anchorEl}
        onClose={() => timeRange.setAnchorEl(null)}
        timeRangeLabel={timeRange.timeRangeLabel}
        onQuickRangeSelect={(r) => {
          const full = REPORTS_QUICK_RANGES.find((q) => q.label === r.label);
          if (full) timeRange.handleQuickRangeSelect(full);
        }}
        onApplyCustomRange={timeRange.handleApplyCustomRange}
        initialFrom={pickerDates.from}
        initialTo={pickerDates.to}
        tzName={timeRange.tzName}
        tzString={timeRange.tzString}
        maxCustomRangeMs={REPORTS_CUSTOM_TIME_MAX_MS}
        quickRanges={REPORTS_QUICK_RANGES}
      />

      {/* Breadcrumb sits directly under the toolbar (matches the dashboard). */}
      <DynamicBreadcrumbs items={breadcrumbItems} />

      {/*
        Mobile-only context strip: shows suite name as a tappable chip
        (opens the dialog with full page context).
      */}
      {isMobile && config && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            color="secondary"
            label={`Suite: ${suiteName}`}
            onClick={() => setMobileContextDialogOpen(true)}
            sx={{ fontWeight: 600 }}
          />
        </Stack>
      )}

      <Dialog
        open={mobileContextDialogOpen}
        onClose={() => setMobileContextDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          component="div"
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
        >
          <Typography variant="h6" component="span" fontWeight={600}>Page Details</Typography>
          <Tooltip title="Close">
            <Button
              color="secondary"
              variant="contained"
              size="small"
              onClick={() => setMobileContextDialogOpen(false)}
              sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
              aria-label="Close"
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </Button>
          </Tooltip>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={1.25}>
            <Box>
              <Typography variant="caption" color="text.secondary">User</Typography>
              <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                {displayUserId || '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Report Suite</Typography>
              <Typography variant="body2" fontWeight={600}>{suiteName}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Tenant</Typography>
              <Typography variant="body2">{config?.tenantId || 'n/a'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Site</Typography>
              <Typography variant="body2">{config?.siteId || 'n/a'}</Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => {
              setMobileContextDialogOpen(false);
              goBack();
            }}
          >
            {level1Path ? 'Back' : 'Back to Reports'}
          </Button>
          <Button onClick={() => setMobileContextDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

      {(loadingCfg || loadingSuite) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loadingCfg && !loadingSuite && !errorMsg && drilldownReports.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            This suite has no drill-down reports
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add reports with mode = "drilldown" to display per-user detail here.
          </Typography>
          {config && suite && (
            <Button
              variant="outlined"
              color="secondary"
              href={`/#/infralytiqs/settings/${config._id}/suites/${suite._id}`}
            >
              Manage Reports
            </Button>
          )}
        </Box>
      )}

      {!loadingCfg && !loadingSuite && config && drilldownReports.length > 0 && (
        <Box
          /*
            `minmax(0, 1fr)` lets grid children with intrinsic min-content
            (wide tables, maps) shrink to fit the column on mobile. With
            plain `1fr` (which is `minmax(auto, 1fr)`) wide panels stretch
            the track and break stacking — this is the same fix applied on
            /#/infralytiqs/reports.
          */
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(12, minmax(0, 1fr))' },
          }}
        >
          {drilldownReports.map((r) => {
            const w = r.layout?.w ?? 12;
            return (
              <Box
                key={r._id}
                sx={{
                  gridColumn: { md: `span ${Math.min(Math.max(w, 3), 12)}` },
                  minWidth: 0,
                  width: '100%',
                }}
              >
                <ReportPanel
                  report={r}
                  tenantId={config.tenantId || ''}
                  siteId={config.siteId || ''}
                  timeRangeOverride={timeRangeOverride}
                  runtimeFilters={runtimeFilters}
                  contextBadge={`user_id = ${displayUserId}`}
                  timezone={suite?.timezone}
                  onEdit={canEditReportPanels ? (rep) => {
                    if (!config?._id || !suiteId || !rep._id) return;
                    navigate(`/infralytiqs/settings/${config._id}/suites/${suiteId}?editReport=${rep._id}`);
                  } : undefined}
                />
              </Box>
            );
          })}
        </Box>
      )}

      {/*
        Spacer — gives the ReportPanel stack a little breathing room below
        the last panel on long pages (matches the settings suite layout).
      */}
      <Box sx={{ height: theme.spacing(4) }} aria-hidden />
    </Box>
  );
};
